// ============================================================================
// BOQ — Project items hook (BOQ line items per project)
// ============================================================================
// Fetches boq.project_items for a project, joined to boq.item_versions for
// the descriptive fields. Supports create, update quantity / zone /
// description_override / sequence_order, and soft delete (DELETE because
// project_items has no deleted_at column — hard delete via RLS).
//
// RLS: gated on can_write_project(project_id) for writes.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';

const SELECT = `
  id, project_id, project_zone_id, item_version_id, quantity, sequence_order,
  description_override, measure_basis, line_type, trade, subtrade, unit, scope_party_id,
  option_group, is_selected,
  rate_quoted, rate_billed, supply_rate_quoted, install_rate_quoted, supply_rate_billed, install_rate_billed, amount_quoted, amount_billed,
  locked_at, created_at, updated_at,
  item_versions:item_version_id (
    id, item_code, version_letter, full_code, short_description, unit, measurement_ref
  )
`.replace(/\s+/g, ' ');

const mapRow = (r) => ({
  id: r.id,
  projectId: r.project_id,
  projectZoneId: r.project_zone_id,
  itemVersionId: r.item_version_id,
  quantity: r.quantity,
  sequenceOrder: r.sequence_order,
  descriptionOverride: r.description_override,
  measureBasis: r.measure_basis,
  lineType: r.line_type || 'measured',
  trade: r.trade,
  subtrade: r.subtrade,
  unit: r.unit,
  scopePartyId: r.scope_party_id,
  optionGroup: r.option_group || null,
  isSelected: r.is_selected !== false,
  rateQuoted: r.rate_quoted,
  rateBilled: r.rate_billed,
  supplyRateQuoted: r.supply_rate_quoted,
  installRateQuoted: r.install_rate_quoted,
  supplyRateBilled: r.supply_rate_billed,
  installRateBilled: r.install_rate_billed,
  amountQuoted: r.amount_quoted,
  amountBilled: r.amount_billed,
  lockedAt: r.locked_at,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
  version: r.item_versions ? {
    id: r.item_versions.id,
    itemCode: r.item_versions.item_code,
    versionLetter: r.item_versions.version_letter,
    fullCode: r.item_versions.full_code,
    shortDescription: r.item_versions.short_description,
    unit: r.item_versions.unit,
    measurementRef: r.item_versions.measurement_ref,
  } : null,
});

// Fields the undo stack snapshots for an update. Maps DB payload keys to the
// camelCase item fields they overwrite.
const UPDATE_FIELDS = {
  quantity: 'quantity',
  projectZoneId: 'projectZoneId',
  descriptionOverride: 'descriptionOverride',
  sequenceOrder: 'sequenceOrder',
  measureBasis: 'measureBasis',
  rateQuoted: 'rateQuoted',
  rateBilled: 'rateBilled',
  supplyRateQuoted: 'supplyRateQuoted',
  installRateQuoted: 'installRateQuoted',
  supplyRateBilled: 'supplyRateBilled',
  installRateBilled: 'installRateBilled',
  scopePartyId: 'scopePartyId',
  optionGroup: 'optionGroup',
  isSelected: 'isSelected',
};

export const useBoqProjectItems = (boqProjectId) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // App-level undo stack. Each entry is an inverse action to replay. Lives in
  // a ref so pushes don't re-render; a counter mirrors the depth for the UI.
  const undoStack = useRef([]);
  const [undoDepth, setUndoDepth] = useState(0);
  const syncDepth = useCallback(() => setUndoDepth(undoStack.current.length), []);
  const pushUndo = useCallback((entry) => {
    undoStack.current.push(entry);
    setUndoDepth(undoStack.current.length);
  }, []);

  // Reset the stack whenever the project changes.
  useEffect(() => { undoStack.current = []; syncDepth(); }, [boqProjectId, syncDepth]);

  const fetchAll = useCallback(async () => {
    if (!boqProjectId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .schema('boq')
        .from('project_items')
        .select(SELECT)
        .eq('project_id', boqProjectId)
        .order('sequence_order')
        .order('created_at');
      if (fetchErr) throw fetchErr;
      setItems((data || []).map(mapRow));
    } catch (err) {
      console.error('[useBoqProjectItems] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'project items' }));
    } finally {
      setLoading(false);
    }
  }, [boqProjectId]);

  // Keep a ref to current items so mutations can snapshot previous values
  // for undo without taking items as a dependency.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const insertRows = useCallback(async (inputs) => {
    const payload = inputs.map((input) => ({
      project_id: boqProjectId,
      project_zone_id: input.projectZoneId || null,
      item_version_id: input.itemVersionId || null,
      quantity: Number(input.quantity) || 0,
      sequence_order: input.sequenceOrder ?? 0,
      description_override: input.descriptionOverride?.trim() || null,
      line_type: input.lineType || 'measured',
      // Free-text classification carried on non-library (allowance) lines. The
      // "Add allowance line" picker writes canonical catalogue labels here; a
      // measured line leaves these null and inherits from its item_version.
      trade: input.trade != null ? String(input.trade).trim() || null : null,
      subtrade: input.subtrade != null ? String(input.subtrade).trim() || null : null,
      unit: input.unit != null ? String(input.unit).trim() || null : null,
      measure_basis: input.measureBasis || null,
      rate_quoted: (input.rateQuoted === '' || input.rateQuoted == null) ? null : Number(input.rateQuoted),
      rate_billed: (input.rateBilled === '' || input.rateBilled == null) ? null : Number(input.rateBilled),
      option_group: input.optionGroup ? String(input.optionGroup).trim() || null : null,
      is_selected: input.isSelected === undefined ? true : !!input.isSelected,
    }));
    const { data, error: insertErr } = await supabase
      .schema('boq')
      .from('project_items')
      .insert(payload)
      .select(SELECT);
    if (insertErr) throw insertErr;
    const mapped = (data || []).map(mapRow);
    setItems((prev) => [...prev, ...mapped]);
    return mapped;
  }, [boqProjectId]);

  const createItem = useCallback(async (input, { recordUndo = true } = {}) => {
    if (!boqProjectId) return { success: false, error: 'No BOQ project context.' };
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to add a line.' };
      }
      const [mapped] = await insertRows([input]);
      if (recordUndo) pushUndo({ type: 'delete-created', ids: [mapped.id] });
      return { success: true, item: mapped };
    } catch (err) {
      console.error('[useBoqProjectItems] create failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'BOQ line' }) };
    }
  }, [boqProjectId, insertRows, pushUndo]);

  // Bulk insert. Used by xlsx import and paste. Single round-trip;
  // transactional via Postgres.
  const createMany = useCallback(async (inputs, { recordUndo = true } = {}) => {
    if (!boqProjectId) return { success: false, error: 'No BOQ project context.' };
    if (!Array.isArray(inputs) || inputs.length === 0) {
      return { success: true, items: [] };
    }
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to import lines.' };
      }
      const mapped = await insertRows(inputs);
      if (recordUndo) pushUndo({ type: 'delete-created', ids: mapped.map((m) => m.id) });
      return { success: true, items: mapped };
    } catch (err) {
      console.error('[useBoqProjectItems] createMany failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'import', entity: 'BOQ lines' }) };
    }
  }, [boqProjectId, insertRows, pushUndo]);

  const updateItem = useCallback(async (id, input, { recordUndo = true } = {}) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to update a line.' };
      }
      const payload = {};
      if (input.quantity !== undefined) payload.quantity = Number(input.quantity);
      if (input.projectZoneId !== undefined) payload.project_zone_id = input.projectZoneId || null;
      if (input.descriptionOverride !== undefined) payload.description_override = input.descriptionOverride?.trim() || null;
      if (input.sequenceOrder !== undefined) payload.sequence_order = input.sequenceOrder;
      if (input.measureBasis !== undefined) payload.measure_basis = input.measureBasis || null;
      if (input.rateQuoted !== undefined) payload.rate_quoted = (input.rateQuoted === '' || input.rateQuoted === null) ? null : Number(input.rateQuoted);
      if (input.rateBilled !== undefined) payload.rate_billed = (input.rateBilled === '' || input.rateBilled === null) ? null : Number(input.rateBilled);
      if (input.supplyRateQuoted !== undefined) payload.supply_rate_quoted = (input.supplyRateQuoted === '' || input.supplyRateQuoted === null) ? null : Number(input.supplyRateQuoted);
      if (input.installRateQuoted !== undefined) payload.install_rate_quoted = (input.installRateQuoted === '' || input.installRateQuoted === null) ? null : Number(input.installRateQuoted);
      if (input.supplyRateBilled !== undefined) payload.supply_rate_billed = (input.supplyRateBilled === '' || input.supplyRateBilled === null) ? null : Number(input.supplyRateBilled);
      if (input.installRateBilled !== undefined) payload.install_rate_billed = (input.installRateBilled === '' || input.installRateBilled === null) ? null : Number(input.installRateBilled);
      if (input.scopePartyId !== undefined) payload.scope_party_id = input.scopePartyId || null;
      if (input.optionGroup !== undefined) {
        const trimmed = (input.optionGroup || '').trim();
        payload.option_group = trimmed || null;
      }
      if (input.isSelected !== undefined) payload.is_selected = !!input.isSelected;

      // Snapshot previous values for the fields we're about to change.
      const before = itemsRef.current.find((p) => p.id === id);
      const inverse = {};
      if (before) {
        Object.keys(input).forEach((k) => {
          if (UPDATE_FIELDS[k]) inverse[k] = before[UPDATE_FIELDS[k]];
        });
      }

      const { data, error: updateErr } = await supabase
        .schema('boq')
        .from('project_items')
        .update(payload)
        .eq('id', id)
        .select(SELECT)
        .single();
      if (updateErr) throw updateErr;
      const mapped = mapRow(data);
      setItems((prev) => prev.map((p) => (p.id === id ? mapped : p)));
      if (recordUndo && before) pushUndo({ type: 'restore-update', id, inverse });
      return { success: true, item: mapped };
    } catch (err) {
      console.error('[useBoqProjectItems] update failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'BOQ line' }) };
    }
  }, [pushUndo]);

  const deleteItem = useCallback(async (id, { recordUndo = true } = {}) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to delete a line.' };
      }
      const before = itemsRef.current.find((p) => p.id === id);
      const { error: deleteErr } = await supabase
        .schema('boq')
        .from('project_items')
        .delete()
        .eq('id', id);
      if (deleteErr) throw deleteErr;
      setItems((prev) => prev.filter((p) => p.id !== id));
      if (recordUndo && before) {
        pushUndo({
          type: 'recreate',
          input: {
            itemVersionId: before.itemVersionId,
            quantity: before.quantity,
            projectZoneId: before.projectZoneId,
            descriptionOverride: before.descriptionOverride,
            sequenceOrder: before.sequenceOrder,
          },
        });
      }
      return { success: true };
    } catch (err) {
      console.error('[useBoqProjectItems] delete failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'BOQ line' }) };
    }
  }, [pushUndo]);

  // Duplicate an existing line — a full-fidelity copy of every content column,
  // inserted immediately after the source (same sequence_order, so it sorts
  // right after it on the next fetch too). The copy carries no take-off
  // measurements, so its quantity becomes a plain manual figure the user can
  // adjust. amount_quoted / amount_billed are generated columns and must not be
  // written. A line that belongs to an option group is copied as an *alternate*
  // (is_selected = false) so the group keeps exactly one selected line.
  const duplicateItem = useCallback(async (id, { recordUndo = true } = {}) => {
    if (!boqProjectId) return { success: false, error: 'No BOQ project context.' };
    const source = itemsRef.current.find((p) => p.id === id);
    if (!source) return { success: false, error: 'Line not found.' };
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to duplicate a line.' };
      }
      const num = (v) => (v == null || v === '' ? null : Number(v));
      const payload = {
        project_id: boqProjectId,
        project_zone_id: source.projectZoneId || null,
        item_version_id: source.itemVersionId || null,
        quantity: Number(source.quantity) || 0,
        sequence_order: source.sequenceOrder ?? 0,
        description_override: source.descriptionOverride?.trim() || null,
        measure_basis: source.measureBasis || null,
        line_type: source.lineType || 'measured',
        trade: source.trade || null,
        subtrade: source.subtrade || null,
        unit: source.unit || null,
        scope_party_id: source.scopePartyId || null,
        rate_quoted: num(source.rateQuoted),
        rate_billed: num(source.rateBilled),
        supply_rate_quoted: num(source.supplyRateQuoted),
        install_rate_quoted: num(source.installRateQuoted),
        supply_rate_billed: num(source.supplyRateBilled),
        install_rate_billed: num(source.installRateBilled),
        option_group: source.optionGroup || null,
        is_selected: source.optionGroup ? false : true,
      };
      const { data, error: insertErr } = await supabase
        .schema('boq')
        .from('project_items')
        .insert(payload)
        .select(SELECT)
        .single();
      if (insertErr) throw insertErr;
      const mapped = mapRow(data);
      // Splice the copy in right after its source so it lands in place
      // immediately (the next fetch orders it there via sequence_order too).
      setItems((prev) => {
        const idx = prev.findIndex((p) => p.id === id);
        if (idx === -1) return [...prev, mapped];
        const next = prev.slice();
        next.splice(idx + 1, 0, mapped);
        return next;
      });
      if (recordUndo) pushUndo({ type: 'delete-created', ids: [mapped.id] });
      return { success: true, item: mapped };
    } catch (err) {
      console.error('[useBoqProjectItems] duplicate failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'duplicate', entity: 'BOQ line' }) };
    }
  }, [boqProjectId, pushUndo]);

  // Bulk-assign the scope party to many lines in one round-trip. Used by the
  // "assign by trade / all lines" action; relies on the autosave restore-point
  // (beforeBulk) rather than the per-line undo stack.
  const setScopeForItems = useCallback(async (ids, scopePartyId) => {
    if (!ids || ids.length === 0) return { success: true, count: 0 };
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to update lines.' };
      }
      const { data, error: uErr } = await supabase
        .schema('boq')
        .from('project_items')
        .update({ scope_party_id: scopePartyId || null })
        .in('id', ids)
        .select(SELECT);
      if (uErr) throw uErr;
      const mapped = (data || []).map(mapRow);
      const byId = new Map(mapped.map((m) => [m.id, m]));
      setItems((prev) => prev.map((p) => byId.get(p.id) || p));
      return { success: true, count: mapped.length };
    } catch (err) {
      console.error('[useBoqProjectItems] setScopeForItems failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'scope' }) };
    }
  }, []);

  // Select one row in an option group: clear every other alternate's
  // is_selected, then set this one's true. Two round-trips on purpose — the
  // partial swap order (false first) keeps the DB invariant clean even if a
  // future migration tightens "at most one selected" to a constraint.
  const selectOptionInGroup = useCallback(async (itemId) => {
    if (!boqProjectId) return { success: false, error: 'No BOQ project context.' };
    const chosen = itemsRef.current.find((p) => p.id === itemId);
    if (!chosen) return { success: false, error: 'Line not found.' };
    if (!chosen.optionGroup) return { success: false, error: 'Line is not part of an option group.' };
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to switch options.' };
      }

      const groupMembers = itemsRef.current.filter((p) =>
        p.projectId === chosen.projectId && p.optionGroup === chosen.optionGroup);
      const previouslySelected = groupMembers.filter((p) => p.isSelected && p.id !== chosen.id);

      // 1) Clear other alternates in this group.
      if (previouslySelected.length > 0) {
        const { error: clearErr } = await supabase
          .schema('boq')
          .from('project_items')
          .update({ is_selected: false })
          .eq('project_id', chosen.projectId)
          .eq('option_group', chosen.optionGroup)
          .neq('id', chosen.id);
        if (clearErr) throw clearErr;
      }

      // 2) Mark the chosen one selected (also re-fetches the row for the cache).
      const { data, error: pickErr } = await supabase
        .schema('boq')
        .from('project_items')
        .update({ is_selected: true })
        .eq('id', chosen.id)
        .select(SELECT)
        .single();
      if (pickErr) throw pickErr;

      const mappedPicked = mapRow(data);
      const clearedIds = new Set(previouslySelected.map((p) => p.id));
      setItems((prev) => prev.map((p) => {
        if (p.id === mappedPicked.id) return mappedPicked;
        if (clearedIds.has(p.id)) return { ...p, isSelected: false };
        return p;
      }));
      return { success: true, item: mappedPicked };
    } catch (err) {
      console.error('[useBoqProjectItems] selectOptionInGroup failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'select', entity: 'option' }) };
    }
  }, [boqProjectId]);

  // Pop the latest action and apply its inverse, without recording a new
  // undo entry. Recreate produces a new row id (DB can't reuse the old one).
  const undo = useCallback(async () => {
    const entry = undoStack.current.pop();
    syncDepth();
    if (!entry) return { success: false, error: 'Nothing to undo.' };
    if (entry.type === 'restore-update') {
      return updateItem(entry.id, entry.inverse, { recordUndo: false });
    }
    if (entry.type === 'delete-created') {
      try {
        for (const id of entry.ids) {
          // eslint-disable-next-line no-await-in-loop
          await deleteItem(id, { recordUndo: false });
        }
        return { success: true };
      } catch (err) {
        return { success: false, error: formatSupabaseError(err, { action: 'undo', entity: 'BOQ line' }) };
      }
    }
    if (entry.type === 'recreate') {
      return createItem(entry.input, { recordUndo: false });
    }
    return { success: false, error: 'Unknown undo action.' };
  }, [updateItem, deleteItem, createItem, syncDepth]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return {
    items, loading, error, refresh: fetchAll,
    createItem, createMany, updateItem, deleteItem, duplicateItem, setScopeForItems,
    selectOptionInGroup,
    undo, canUndo: undoDepth > 0, undoDepth,
  };
};
